import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { Button } from '@/components/Button'
import { Kicker } from '@/components/Kicker'
import { PACK_COLORS } from '@/design-system/tokens'

/**
 * Current version of the data-handling notice. Bump this SMALLINT any time the copy
 * materially changes (new data type collected, new data-sharing partner, new region).
 * Users whose stored `consent_version` is below this number are re-prompted.
 */
export const CONSENT_VERSION = 1

/**
 * ConsentModal — first-run acknowledgment overlay.
 * Renders as a fullscreen overlay when the signed-in user's public.users.consent_version
 * is null or < CONSENT_VERSION. Blocks interaction with the rest of the app until accepted.
 *
 * Only shown to authenticated users (needs user id to write consent). AuthScreen doesn't
 * need consent — that flow only touches auth email + password, which is table stakes.
 */
export function ConsentModal({ onAccepted }: { onAccepted: () => void }) {
  const user = useAppStore((s) => s.user)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    // Prevent background scroll while modal is up
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  async function accept() {
    if (!user) return
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase
        .from('users')
        .update({
          consented_at: new Date().toISOString(),
          consent_version: CONSENT_VERSION,
        })
        .eq('id', user.id)
      if (error) throw new Error(error.message)
      onAccepted()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-bg-base/95 backdrop-blur-md overflow-y-auto">
      <div className="mx-auto min-h-svh max-w-lg px-6 py-safe pt-12 pb-8 flex flex-col">
        <Kicker color={PACK_COLORS.early_care.light}>Before you begin</Kicker>
        <h1 className="mt-3 font-serif italic text-hero text-text-primary">
          What we collect. Where it lives.
        </h1>

        <div className="mt-6 space-y-5 text-body text-text-secondary flex-1">
          <section>
            <div className="text-body font-semibold text-text-primary">What Wren stores</div>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li>Names, dates of birth, due dates, and photos of the people in your family that you add</li>
              <li>Feedings, diapers, sleep, medications, notes, and other care events you log</li>
              <li>Growth measurements and documents (insurance, immunization, etc) you upload</li>
              <li>Your email and account credentials</li>
            </ul>
          </section>

          <section>
            <div className="text-body font-semibold text-text-primary">Where it lives</div>
            <p className="mt-2">
              Every byte is stored in a Supabase Postgres database hosted in Canada (Central region).
              Files (documents, images) live in Supabase Storage in the same region. Nothing goes to
              a third-party analytics service, ad network, or LLM provider — the Ask Wren feature
              queries your data directly and does not train any model.
            </p>
          </section>

          <section>
            <div className="text-body font-semibold text-text-primary">Who sees it</div>
            <p className="mt-2">
              Only members of your family (people you explicitly invite) can read your family's data.
              Row-level security in the database enforces this — even if someone had your invite link
              without an account, they can't see anything until they sign in with the email the invite
              was sent to.
            </p>
          </section>

          <section>
            <div className="text-body font-semibold text-text-primary">Your control</div>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li>Correct or delete individual entries any time from the Timeline</li>
              <li>Download every byte of your family's data as JSON from Settings → Privacy</li>
              <li>Delete your account entirely from Settings → Danger zone — it wipes your data on the server</li>
              <li>Revoke caregivers from Family → Pending invites at any time</li>
            </ul>
          </section>

          <section className="text-caption text-text-tertiary">
            Wren does not sell, share, or use your data for advertising. This is a personal-first
            product. Your record is yours.
          </section>
        </div>

        {err && (
          <div className="mt-4 rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <div className="mt-6 space-y-2 sticky bottom-0 bg-bg-base pt-3">
          <Button
            variant="primary"
            size="lg"
            packColor={PACK_COLORS.early_care.light}
            loading={busy}
            onClick={accept}
            className="w-full"
          >
            I understand — continue
          </Button>
        </div>
      </div>
    </div>
  )
}
